/**
 * voice-v8 — Projects-API-Client (Phase E3).
 *
 * Wrappt /api/projects/*. Verwendet Session-Cookie für User-Resolution.
 */

import type {
  ProjectLinkRecord,
  ProjectLinkType,
  ProjectRecord,
} from "../../memory/projects.js";

export type { ProjectLinkType };
export type ProjectDto = ProjectRecord;
export type ProjectLinkDto = ProjectLinkRecord;

interface ProjectWithLinks {
  project: ProjectDto;
  links: ProjectLinkDto[];
}

export async function listProjects(
  opts: { archived?: boolean } = {},
): Promise<ProjectDto[]> {
  const url = new URL("/api/projects", window.location.origin);
  if (opts.archived) url.searchParams.set("archived", "1");
  const res = await fetch(url.toString(), { credentials: "same-origin" });
  if (!res.ok) throw new Error(`list projects failed: ${res.status}`);
  const data = (await res.json()) as { items: ProjectDto[] };
  return data.items;
}

/**
 * Detail-Endpoint des Servers liefert `{project, links}`. Wrapper hier
 * ist immer die "voll"-Version — die alte `ProjectDto`-Only-Variante hat
 * keine Aufrufer mehr.
 */
export async function getProject(id: string): Promise<ProjectWithLinks> {
  const res = await fetch(`/api/projects/${encodeURIComponent(id)}`, {
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`get project failed: ${res.status}`);
  return (await res.json()) as ProjectWithLinks;
}

export async function createProject(input: {
  name: string;
  description?: string;
  color?: string;
}): Promise<ProjectDto> {
  const res = await fetch("/api/projects", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `create project failed: ${res.status} ${text.slice(0, 200)}`,
    );
  }
  return (await res.json()) as ProjectDto;
}

export async function updateProject(
  id: string,
  patch: { name?: string; description?: string | null; color?: string | null },
): Promise<ProjectDto> {
  const res = await fetch(`/api/projects/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`update project failed: ${res.status}`);
  return (await res.json()) as ProjectDto;
}

export async function archiveProject(id: string): Promise<void> {
  const res = await fetch(`/api/projects/${encodeURIComponent(id)}/archive`, {
    method: "POST",
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`archive failed: ${res.status}`);
}

export async function unarchiveProject(id: string): Promise<void> {
  const res = await fetch(`/api/projects/${encodeURIComponent(id)}/unarchive`, {
    method: "POST",
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`unarchive failed: ${res.status}`);
}

export async function deleteProject(id: string): Promise<void> {
  const res = await fetch(`/api/projects/${encodeURIComponent(id)}`, {
    method: "DELETE",
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`delete project failed: ${res.status}`);
}

export async function getActiveProject(): Promise<ProjectDto | null> {
  const res = await fetch("/api/projects/active", {
    credentials: "same-origin",
  });
  if (!res.ok) return null;
  // Pre.200: Server liefert { active: ... }, nicht { project: ... }.
  // Dieser Schlüssel-Mismatch hat alle Active-Project-Anzeigen
  // (Topbar, Subheader-Bar, Footer-Slot) seit Wochen hidden gehalten.
  const data = (await res.json()) as {
    active?: ProjectDto | null;
    project?: ProjectDto | null;
  };
  return data.active ?? data.project ?? null;
}

export async function setActiveProject(
  projectId: string | null,
): Promise<void> {
  const res = await fetch("/api/projects/active", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ project_id: projectId }),
  });
  if (!res.ok) throw new Error(`set active failed: ${res.status}`);
}

export async function linkToProject(
  projectId: string,
  targetType: ProjectLinkType,
  targetId: string,
): Promise<void> {
  const res = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/links`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ target_type: targetType, target_id: targetId }),
    },
  );
  if (!res.ok) throw new Error(`link failed: ${res.status}`);
}

export async function unlinkFromProject(
  projectId: string,
  targetType: ProjectLinkType,
  targetId: string,
): Promise<void> {
  const url = new URL(
    `/api/projects/${encodeURIComponent(projectId)}/links`,
    window.location.origin,
  );
  url.searchParams.set("target_type", targetType);
  url.searchParams.set("target_id", targetId);
  const res = await fetch(url.toString(), {
    method: "DELETE",
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`unlink failed: ${res.status}`);
}

// --- Sprint B: project_files-API ----------------------------------------

export type ProjectFileType =
  | "chapter"
  | "character"
  | "note"
  | "recording"
  | "transcript"
  | "reference"
  | "pdf"
  | "image"
  | "vocabulary"
  | "other";

export interface ProjectFileDto {
  id: string;
  projectId: string;
  type: ProjectFileType;
  title: string;
  path: string;
  mime: string | null;
  sizeBytes: number | null;
  createdAt: string;
  updatedAt: string | null;
  /** Pre.980: Autor/Quelle (extern), null = unbekannt/eigenes. */
  author?: string | null;
}

export async function listProjectFiles(
  projectId: string,
  opts: { type?: ProjectFileType } = {},
): Promise<ProjectFileDto[]> {
  const url = new URL(
    `/api/projects/${encodeURIComponent(projectId)}/files`,
    window.location.origin,
  );
  if (opts.type) url.searchParams.set("type", opts.type);
  const res = await fetch(url.toString(), { credentials: "same-origin" });
  if (!res.ok) throw new Error(`list files failed: ${res.status}`);
  const data = (await res.json()) as { items: ProjectFileDto[] };
  return data.items;
}

/** Pre.975: gekürzte Vorlese-Hörfassung einer Datei. */
interface NarrationDto {
  status: "pending" | "done" | "failed";
  body: string | null;
  error: string | null;
}

export async function getProjectFile(
  projectId: string,
  fileId: string,
): Promise<{
  item: ProjectFileDto;
  content: string | null;
  binary: boolean;
  narration?: NarrationDto | null;
}> {
  const res = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/files/${encodeURIComponent(fileId)}`,
    { credentials: "same-origin" },
  );
  if (!res.ok) throw new Error(`get file failed: ${res.status}`);
  return res.json();
}

/** Pre.975: Hörfassung für ein (bestehendes) Dokument anstoßen. */
export async function narrateProjectFile(
  projectId: string,
  fileId: string,
): Promise<{ status: string; tooShort?: boolean }> {
  const res = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/files/${encodeURIComponent(fileId)}/narrate`,
    { method: "POST", credentials: "same-origin" },
  );
  if (!res.ok) throw new Error(`narrate failed: ${res.status}`);
  return res.json();
}

/**
 * Eine Text-Projekt-Datei als MP3 vertonen und dauerhaft als Projekt-Audiodatei
 * speichern (2026-07-13, Martin). provider "google" nutzt native Stimmen
 * (z.B. es-ES-Chirp3-HD-Aoede fuer Spanisch) ohne ElevenLabs-Guthaben.
 */
export async function saveProjectFileAsMp3(
  projectId: string,
  fileId: string,
  opts: { voiceId?: string; provider?: string; title?: string } = {},
): Promise<{ ok: boolean; fileId?: string; voice?: string }> {
  const res = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/tts-mp3`,
    {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fileId, ...opts }),
    },
  );
  if (!res.ok) throw new Error(`tts-mp3 failed: ${res.status}`);
  return res.json();
}

export async function createProjectFile(
  projectId: string,
  input: {
    title: string;
    content: string;
    type: ProjectFileType;
    author?: string;
  },
): Promise<ProjectFileDto> {
  const res = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/files`,
    {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    },
  );
  if (!res.ok) throw new Error(`create file failed: ${res.status}`);
  const data = (await res.json()) as { item: ProjectFileDto };
  return data.item;
}

export async function updateProjectFile(
  projectId: string,
  fileId: string,
  patch: { content?: string; title?: string; author?: string | null },
): Promise<ProjectFileDto> {
  const res = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/files/${encodeURIComponent(fileId)}`,
    {
      method: "PUT",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    },
  );
  if (!res.ok) throw new Error(`update file failed: ${res.status}`);
  const data = (await res.json()) as { item: ProjectFileDto };
  return data.item;
}

export async function deleteProjectFile(
  projectId: string,
  fileId: string,
): Promise<void> {
  const res = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/files/${encodeURIComponent(fileId)}`,
    { method: "DELETE", credentials: "same-origin" },
  );
  if (!res.ok) throw new Error(`delete file failed: ${res.status}`);
}

/** Sprint C: Multipart-Upload (PDF, Image, Audio). */
export async function uploadProjectFile(
  projectId: string,
  file: File,
  title?: string,
): Promise<ProjectFileDto> {
  const fd = new FormData();
  fd.append("file", file);
  if (title) fd.append("title", title);
  const res = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/files/upload`,
    { method: "POST", credentials: "same-origin", body: fd },
  );
  if (!res.ok) {
    let detail = "";
    try {
      const j = (await res.json()) as { error?: string };
      detail = j.error ?? "";
    } catch {
      /* */
    }
    throw new Error(
      `upload failed: ${res.status}${detail ? " " + detail : ""}`,
    );
  }
  const data = (await res.json()) as { item: ProjectFileDto };
  return data.item;
}

/**
 * Lädt ein PDF hoch, der Server extrahiert den Text und legt ihn — zusammen
 * mit den optionalen Metafeldern (Titel, Beschreibung, Typ, Schlagwort) — als
 * durchsuchbare Datei mit Embedding ab. Wie `uploadProjectFile`, aber gegen
 * die Extraktions-Route. Die Felder sind optional (abwärtskompatibel): fehlen
 * sie, verhält sich die Route wie zuvor (Typ `note`, Titel aus Dateiname).
 */
/**
 * 2026-07-14 (Martin, "MP3s hochladen — Teil meiner Geschichte"): Binaer-Datei
 * (Audio/MP3 u.a.) in ein Projekt laden. Nutzt die bestehende Server-Route
 * /files/upload (inferType: audio/* -> "recording" -> abspielbar im Viewer).
 */
export async function uploadBinaryToProject(
  projectId: string,
  file: File,
  title?: string,
): Promise<ProjectFileDto> {
  const fd = new FormData();
  fd.append("file", file);
  if (title) fd.append("title", title);
  const res = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/files/upload`,
    { method: "POST", credentials: "same-origin", body: fd },
  );
  if (!res.ok) {
    let detail = "";
    try {
      const j = (await res.json()) as { error?: string };
      detail = j.error ?? "";
    } catch {
      /* kein JSON */
    }
    throw new Error(detail || `upload failed: ${res.status}`);
  }
  const data = (await res.json()) as { item: ProjectFileDto };
  return data.item;
}

export async function extractPdfToMaterial(
  projectId: string,
  file: File,
  meta: {
    title?: string;
    description?: string;
    fileType?: ProjectFileType;
    tag?: string;
    author?: string;
  } = {},
): Promise<ProjectFileDto> {
  const fd = new FormData();
  fd.append("file", file);
  if (meta.title) fd.append("title", meta.title);
  if (meta.description) fd.append("description", meta.description);
  if (meta.fileType) fd.append("fileType", meta.fileType);
  if (meta.tag) fd.append("tag", meta.tag);
  if (meta.author) fd.append("author", meta.author);
  const res = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/files/extract-pdf`,
    { method: "POST", credentials: "same-origin", body: fd },
  );
  if (!res.ok) {
    let detail = "";
    try {
      const j = (await res.json()) as { error?: string };
      detail = j.error ?? "";
    } catch {
      /* */
    }
    throw new Error(
      detail || `PDF konnte nicht gelesen werden (${res.status})`,
    );
  }
  const data = (await res.json()) as { item: ProjectFileDto };
  return data.item;
}

// Pre.804 (#73b): Verwandte Erinnerungen (Embedding-Suche, best-effort).
interface RelatedItemDto {
  id: string;
  preview: string;
  score: number;
  createdAt: string;
}

export async function listRelated(
  projectId: string,
): Promise<RelatedItemDto[]> {
  const res = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/related`,
    { credentials: "same-origin" },
  );
  if (!res.ok) throw new Error(`related ${res.status}`);
  const data = (await res.json()) as { items?: RelatedItemDto[] };
  return data.items ?? [];
}

// Pre.804 (#73a): Projekt-Bericht (async — POST startet, GET pollt).
interface ReportStateDto {
  status: "running" | "done" | "error";
  startedAt: string;
  fileId?: string;
  error?: string;
}

// 2026-07-05 (Martin): Projekt aufraeumen — Plan holen (Vorschau) + anwenden.
interface CleanupDuplicate {
  removeId: string;
  removeTitle: string;
  keepTitle: string;
  type: string;
  sizeBytes: number;
}
interface CleanupUploadDuplicate {
  removeUploadId: string;
  removeTitle: string;
  keepTitle: string;
  sizeBytes: number;
}
interface CleanupPlan {
  ok: boolean;
  fileCount: number;
  duplicates: CleanupDuplicate[];
  /** Gleicher Titel+Typ, anderer Inhalt — aeltere Version. */
  nameDuplicates?: CleanupDuplicate[];
  /** Byte-identische Mediathek-Uploads am Projekt. */
  uploadDuplicates?: CleanupUploadDuplicate[];
  bytesSaved: number;
}
export async function getCleanupPlan(projectId: string): Promise<CleanupPlan> {
  const res = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/cleanup-plan`,
    { method: "POST", credentials: "same-origin" },
  );
  if (!res.ok) throw new Error(`cleanup-plan ${res.status}`);
  return (await res.json()) as CleanupPlan;
}
export async function applyCleanup(
  projectId: string,
  fileIds: string[],
  uploadIds: string[] = [],
): Promise<{ ok: boolean; deleted: string[]; skipped: string[] }> {
  const res = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/cleanup-apply`,
    {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fileIds, uploadIds }),
    },
  );
  if (!res.ok) throw new Error(`cleanup-apply ${res.status}`);
  return (await res.json()) as {
    ok: boolean;
    deleted: string[];
    skipped: string[];
  };
}

export async function requestProjectReport(
  projectId: string,
): Promise<ReportStateDto> {
  const res = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/report`,
    { method: "POST", credentials: "same-origin" },
  );
  // 202 = gestartet, 409 = läuft schon — beides liefert den State.
  if (res.status !== 202 && res.status !== 409) {
    throw new Error(`report ${res.status}`);
  }
  const data = (await res.json()) as { state: ReportStateDto };
  return data.state;
}

export async function getProjectReport(
  projectId: string,
): Promise<ReportStateDto | null> {
  const res = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/report`,
    { credentials: "same-origin" },
  );
  if (!res.ok) return null;
  const data = (await res.json()) as { state: ReportStateDto | null };
  return data.state ?? null;
}

// --- Projekt-Dokument (KI-generiertes Briefing-Dokument) ------------------

interface ProjectDocumentDto {
  document: {
    bodyMd: string;
    version: number;
    wordCount: number;
    updatedAt: string | null;
  } | null;
  versions: Array<{
    id: string;
    version: number;
    wordCount: number;
    archivedAt: string;
  }>;
}

export async function getProjectDocument(
  projectId: string,
): Promise<ProjectDocumentDto> {
  const res = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/document`,
    { credentials: "same-origin" },
  );
  if (!res.ok) throw new Error(`get project document failed: ${res.status}`);
  return (await res.json()) as ProjectDocumentDto;
}

// --- Projekt-Timeline (datierte Meilensteine) ----------------------------

interface ProjectTimelineEvent {
  date: string | null;
  dateText: string | null;
  title: string;
  description: string | null;
}

export async function getProjectTimeline(
  projectId: string,
): Promise<ProjectTimelineEvent[]> {
  const res = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/timeline`,
    { credentials: "same-origin" },
  );
  if (!res.ok) throw new Error("timeline-load-failed");
  const data = (await res.json()) as { events: ProjectTimelineEvent[] };
  return data.events ?? [];
}

export async function regenerateProjectDocument(projectId: string): Promise<{
  ok: boolean;
  updated: boolean;
  reason?: string;
  version?: number;
}> {
  const res = await fetch(
    `/api/projects/${encodeURIComponent(projectId)}/document/regenerate`,
    { method: "POST", credentials: "same-origin" },
  );
  if (!res.ok)
    throw new Error(`regenerate project document failed: ${res.status}`);
  return (await res.json()) as {
    ok: boolean;
    updated: boolean;
    reason?: string;
    version?: number;
  };
}
